import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { SuggestionStatus } from '@dazzle/api-client';

import { DesktopShow, DeviceQueries } from 'Common/components/styled';
import BlueSpinner from 'Common/components/BlueSpinner';
import { NotificationOutlet, useNotificationContext } from 'Common/contexts/NotificationContext';
import { suggestionsUpdateStatusInternal, suggestionUpdateInternal } from 'Common/api/DazzleInternalService';
import { BannerLevel } from 'Common/components/Banner';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/api/SumoLogic';
import { toSuggestion, USuggestion } from 'Common/util/Suggestion';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { Page } from 'Compass/core/MainLayout';
import { PageCard, PageTitle } from 'Compass/components/styled/PageLayout';
import { useSuggestionsContext } from 'Compass/contexts/SuggestionsContext';
import SuggestionRow from 'Compass/components/suggestions/SuggestionRow';
import SuggestionsPagination from 'Compass/components/suggestions/SuggestionsPagination';
import { Button, ButtonTheme } from 'Compass/components/styled/Button';
import { useUserContext } from 'Compass/contexts/UserContext';
import SuggestionFilters from 'Compass/components/suggestions/SuggestionFilters';

const OrderSuggestionsPage = () => {
  const suggestionsContext = useSuggestionsContext();
  const { fetchSuggestions } = suggestionsContext;
  const notificationContext = useNotificationContext();
  const userContext = useUserContext();
  const [selectedSuggestionIds, setSelectedSuggestionIds] = useState(new Set<string>());
  const [submitting, setSubmitting] = useState(false);
  const allCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void fetchSuggestions();
  }, [fetchSuggestions]);

  const suggestions = suggestionsContext.suggestions;

  const onApprove = async (suggestion: USuggestion) => {
    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.UserAction,
      userId: userContext.name,
      message: `[OrderSuggestionsPage] Approved suggestion ${suggestion.suggestionId}.`
    });

    await updateStatus(suggestion, SuggestionStatus.Pending, 'approved');
  };

  const onReject = async (suggestion: USuggestion) => {
    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.UserAction,
      userId: userContext.name,
      message: `[OrderSuggestionsPage] Rejected suggestion ${suggestion.suggestionId}.`
    });

    await updateStatus(suggestion, SuggestionStatus.Invalid, 'rejected');
  };

  const updateStatus = async (suggestion: USuggestion, status: SuggestionStatus, actionText: string) => {
    setSubmitting(true);

    await suggestionUpdateInternal({
      ...toSuggestion(suggestion),
      status
    });

    notificationContext.showBanner({
      level: BannerLevel.INFO,
      contents: `Suggestion "${suggestion.title}" ${actionText}`,
      duration: 5000
    });

    await suggestionsContext.fetchSuggestions();

    setSubmitting(false);
  };

  const onApproveSelected = async () => {
    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.UserAction,
      userId: userContext.name,
      message: `[OrderSuggestionsPage] Approved suggestions [${Array.from(selectedSuggestionIds).join(',')}].`
    });

    await updateStatuses(SuggestionStatus.Pending, 'approved');
  };

  const onRejectSelected = async () => {
    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.UserAction,
      userId: userContext.name,
      message: `[OrderSuggestionsPage] Rejected suggestions [${Array.from(selectedSuggestionIds).join(',')}].`
    });

    await updateStatuses(SuggestionStatus.Invalid, 'rejected');
  };

  const updateStatuses = async (status: SuggestionStatus, actionText: string) => {
    setSubmitting(true);

    await suggestionsUpdateStatusInternal(Array.from(selectedSuggestionIds), status);

    notificationContext.showBanner({
      level: BannerLevel.INFO,
      contents: `${selectedSuggestionIds.size} Suggestion${selectedSuggestionIds.size !== 1 ? 's' : ''} ${actionText}`,
      duration: 5000
    });

    await suggestionsContext.fetchSuggestions();

    if (allCheckboxRef.current) {
      allCheckboxRef.current.checked = false;
      allCheckboxRef.current.indeterminate = false;
    }
    setSelectedSuggestionIds(new Set());
    setSubmitting(false);
  };


  const onRowSelect = (suggestion: USuggestion, selected: boolean) => {
    if (selected) {
      selectedSuggestionIds.add(suggestion.suggestionId);
    } else {
      selectedSuggestionIds.delete(suggestion.suggestionId);
    }

    setSelectedSuggestionIds(new Set(selectedSuggestionIds));

    if (!allCheckboxRef.current) {
      return;
    }

    const allChecked = suggestions?.every(suggestion => selectedSuggestionIds.has(suggestion.suggestion.suggestion_id));
    const noneChecked = suggestions?.every(
      suggestion => !selectedSuggestionIds.has(suggestion.suggestion.suggestion_id));

    if (allChecked) {
      allCheckboxRef.current.checked = true;
      allCheckboxRef.current.indeterminate = false;
    } else if (noneChecked) {
      allCheckboxRef.current.checked = false;
      allCheckboxRef.current.indeterminate = false;
    } else {
      allCheckboxRef.current.checked = false;
      allCheckboxRef.current.indeterminate = true;
    }
  };

  const onAllCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked;

    if (checked) {
      suggestions?.forEach(suggestion => selectedSuggestionIds.add(suggestion.suggestion.suggestion_id));
    } else {
      selectedSuggestionIds.clear();
    }
    setSelectedSuggestionIds(new Set(selectedSuggestionIds));
  };

  const showActions = suggestionsContext.suggestionStatusFilter === SuggestionStatus.PendingReview;

  return <>
    <CompassHeader pageType={PageType.Suggestions}/>
    <Page>
      <NotificationOutlet/>
      <PageCard>
        <PageTitle role="page-title">Order Suggestions</PageTitle>
        <SuggestionsTableContainer>
          {submitting && <Screen/>}
          <SuggestionFilters/>
          <SuggestionsTable $showActions={showActions}>
            <DesktopShow>
              { showActions && <TableHeader><input type="checkbox" ref={allCheckboxRef} onChange={onAllCheckboxClick}/></TableHeader> }
              <TableHeader>Image</TableHeader>
              <TableHeader>Title</TableHeader>
              <TableHeader>Message</TableHeader>
              <TableHeader>Est. Price</TableHeader>
              <TableHeader>Created</TableHeader>
              { showActions && <TableHeader>Actions</TableHeader> }
            </DesktopShow>
            {
              suggestions && suggestions.map(
                suggestion => <SuggestionRow
                  key={suggestion.suggestion.suggestion_id}
                  showActions={showActions}
                  parsedSuggestion={suggestion}
                  selected={selectedSuggestionIds.has(suggestion.suggestion.suggestion_id)}
                  onApprove={onApprove}
                  onReject={onReject}
                  onSelect={onRowSelect}
                />)
            }
            {
              (!suggestions || submitting) && <MessageContainer><BlueSpinner/></MessageContainer>
            }
            {
              suggestions && suggestions?.length === 0 &&
              <MessageContainer>There are no suggestions pending review.</MessageContainer>
            }
          </SuggestionsTable>
          { showActions && <DesktopShow>
            <ActionRow>
              <Button $theme={ButtonTheme.secondary} onClick={onRejectSelected}
                      disabled={selectedSuggestionIds.size == 0}>
                Reject Selected
              </Button>
              <Button onClick={onApproveSelected} disabled={selectedSuggestionIds.size == 0}>
                Approve Selected
              </Button>
            </ActionRow>
          </DesktopShow> }
          <SuggestionsPagination/>
        </SuggestionsTableContainer>
      </PageCard>
    </Page>
  </>;
};


const SuggestionsTableContainer = styled.div`
  margin-top: 24px;
  position: relative;

  @media (${DeviceQueries.mobile}) {
    margin-top: 0;
  }
`;

const SuggestionsTable = styled.div<{$showActions: boolean}>`
  display: grid;
  grid-template-columns: repeat(${({$showActions}) => $showActions ? '2' : '1'}, max-content) 1fr 1fr repeat(${({$showActions}) => $showActions ? '3' : '2'}, max-content);
  gap: 0;
  border: 1px solid #E4E7EF;
  border-radius: 12px;
  overflow: hidden;

  @media (${DeviceQueries.mobile}) {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border: none;
  }`;

const TableHeader = styled.div`
  background: #F8F9FC;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 11px;
  color: #5A6178;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  border-bottom: 1px solid #E4E7EF;
`;

const Screen = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, .3);
`;

const MessageContainer = styled.div`
  grid-column: 1/5;
  padding: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 16px;
`;

export default OrderSuggestionsPage;
